import axios from 'axios';
import { storage, path } from '@commons';
import { httpAuthService } from './http.auth.service';
import { findUserAuthResumed } from '@handler';
import jwt_decode from "jwt-decode";
import store from '../store';

// Interceptador para capturar headers de WebView
export const webviewAuthInterceptor = {
    setupInterceptors,
    validateWebViewHeaders,
    authenticateFromWebView
};

function setupInterceptors() {
    // Interceptador de requisições para capturar headers da WebView
    axios.interceptors.request.use(
        (config) => {
            // Verificar se é uma requisição de WebView auth
            if (config.url && config.url.includes('/webview-auth')) {
                // Preservar headers originais da WebView
                const webviewHeaders = getWebViewHeaders();
                if (webviewHeaders) {
                    config.headers = {
                        ...config.headers,
                        ...webviewHeaders
                    };
                }
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Interceptador de respostas para tratar erros de autenticação
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            // Token expirado/recusado (ex.: troca da chave do JWT): sem isso a tela fica logada
            // mas toda chamada falha. Limpa a sessão e volta ao login.
            const status = error?.response?.status;
            const url = error?.config?.url ?? '';
            const temSessao = !!storage.get(path.token);
            if (status === 401 && temSessao
                && !url.includes('/oauth/token')
                && !url.includes('/webview-auth')
                && window.location.pathname !== '/login') {
                httpAuthService.logout({ path });
                delete axios.defaults.headers.common['Authorization'];
                window.location.assign('/login');
            }
            return Promise.reject(error);
        }
    );
}

function getWebViewHeaders() {
    // SEGURANÇA: Aceitar APENAS meta tags (injetadas antes do JS carregar)
    // NÃO aceitar window.__WEBVIEW_HEADERS__ (pode ser injetado via DevTools)
    const authMeta = document.querySelector('meta[name="webview-auth"]');
    const sourceMeta = document.querySelector('meta[name="webview-source"]');

    if (authMeta && sourceMeta) {
        return {
            'Authorization': authMeta.content,
            'X-Source': sourceMeta.content
        };
    }

    return null;
}

async function validateWebViewHeaders(headers) {
    if (!headers || typeof headers !== 'object') {
        return { 
            valid: false, 
            error: 'Headers WebView não fornecidos ou formato inválido.' 
        };
    }

    const xSource = headers['X-Source'] || headers['x-source'];
    const authorization = headers['Authorization'] || headers['authorization'];
    
    // Validação de origem
    if (xSource !== 'AgrareFlutterApp') {
        return { 
            valid: false, 
            error: 'Origem não autorizada. Esta rota é exclusiva para o aplicativo móvel.' 
        };
    }

    // Validação do token - verificar se existe e não é undefined/null
    if (!authorization || typeof authorization !== 'string') {
        return { 
            valid: false, 
            error: 'Token de autorização ausente ou formato inválido (não é string).' 
        };
    }

    if (!authorization.startsWith('Bearer ')) {
        return { 
            valid: false, 
            error: 'Token de autorização deve começar com "Bearer ".' 
        };
    }

    const token = authorization.replace('Bearer ', '');

    if (!token || token.trim() === '') {
        return {
            valid: false,
            error: 'Token vazio após remover "Bearer ".'
        };
    }

    // SEGURANÇA: Validar estrutura e expiração do JWT
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return {
                valid: false,
                error: 'Token JWT com formato inválido.'
            };
        }

        // Decodificar payload do JWT
        const payload = JSON.parse(atob(parts[1]));

        // Verificar expiração do token
        if (payload.exp) {
            const now = Math.floor(Date.now() / 1000);
            if (payload.exp < now) {
                return {
                    valid: false,
                    error: 'Token expirado. Faça login novamente no aplicativo.'
                };
            }
        }

        // Verificar se tem user_id
        if (!payload.user_id && !payload.sub) {
            return {
                valid: false,
                error: 'Token não contém identificação do usuário.'
            };
        }

    } catch (error) {
        return {
            valid: false,
            error: 'Erro ao validar token JWT.'
        };
    }

    // IMPORTANTE: Este token ainda precisa ser validado no BACKEND
    // A validação acima apenas verifica formato e expiração
    // A assinatura JWT DEVE ser verificada no servidor
    return {
        valid: true,
        token: token,
        headers: headers
    };
}

async function authenticateFromWebView(validationResult) {
    try {
        const { token } = validationResult;
        
        // Armazenar o token na sessão (igual ao login normal)
        await storage.set(path.token, token);
        
        // Configurar headers padrão do axios (ESSENCIAL - faltava isso!)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Decodificar JWT para obter user_id
        const decodedToken = jwt_decode(token);
        const userId = decodedToken.user_id || decodedToken.sub;
        
        if (!userId) {
            throw new Error('Token JWT não contém informações do usuário');
        }
        
        let user;

        // SEGURANÇA: Validar token JWT no backend com assinatura
        try {
            // console.log('🔐 Validando token JWT no backend...');

            // Validar token no backend (endpoint seguro com validação de assinatura)
            const response = await axios.post(
                `${path.baseManager}/webview-auth/validate-token`,
                { token },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Source': 'AgrareFlutterApp',
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.data.valid) {
                throw new Error(response.data.error || 'Token inválido');
            }

            user = response.data.user;
            // console.log('✅ Token validado com sucesso no backend (assinatura verificada)');

        } catch (backendError) {
            // console.error('❌ Erro ao validar token no backend:', backendError);

            // Se for erro de validação (401/403), não usar fallback
            if (backendError.response && [401, 403].includes(backendError.response.status)) {
                const errorMsg = backendError.response.data?.error || 'Token inválido ou expirado';
                throw new Error(`Falha na autenticação: ${errorMsg}. Faça login novamente no aplicativo.`);
            }

            // Se for erro de rede/servidor, tentar fallback temporário
            // console.warn('⚠️ Erro de comunicação com backend. Tentando fallback...');

            try {
                user = await findUserAuthResumed(userId);
                // console.log('✅ Dados do usuário obtidos via fallback (sem validação de assinatura)');
                // console.warn('⚠️ ATENÇÃO: Token não foi validado no servidor!');
            } catch (fallbackError) {
                // console.error('❌ Fallback também falhou:', fallbackError);
                throw new Error('Não foi possível validar suas credenciais. Verifique sua conexão e tente novamente.');
            }
        }
        
        // Atualizar sessão do usuário usando método existente
        await httpAuthService.updateSesssionUser(user);
        
        // ESSENCIAL: Atualizar Redux store para que as rotas reconheçam o usuário
        store.dispatch({
            type: 'UPDATE_SESSION_USER',
            user: user
        });

        // Configurar credenciais do axios (igual ao login normal)
        axios.defaults.withCredentials = true;

        // Determinar caminho de redirecionamento baseado no estado do usuário
        let redirectPath = '/dashboard';

        // Se o usuário não tem schemes e não é suporte, redirecionar para onboarding
        if (user.countSchemes === 0 && user.perfil !== 'SUPORTE_AGRARE') {
            redirectPath = user.perfil === 'ESTUDANTE_PESQUISA' ? '/conta-estudante' : '/nova-conta';
        }

        return {
            success: true,
            user: user,
            redirectPath: redirectPath
        };
        
    } catch (error) {
        console.error('Erro na autenticação via WebView:', error);
        return {
            success: false,
            error: `Falha interna na autenticação: ${error.message}. Tente fazer login web normal.`
        };
    }
}